import { memo } from "react";
import { Wifi, WifiOff } from "lucide-react";
import { ConnectionQuality } from "@/hooks/useConnectionQuality";

interface ConnectionQualityIndicatorProps {
  quality: ConnectionQuality;
  showLabel?: boolean;
}

const qualityConfig: Record<ConnectionQuality, { bars: number; color: string; label: string }> = {
  excellent: { bars: 4, color: "bg-green-500", label: "Excellent" },
  good: { bars: 3, color: "bg-green-400", label: "Good" },
  fair: { bars: 2, color: "bg-yellow-500", label: "Fair" },
  poor: { bars: 1, color: "bg-red-500", label: "Poor" },
  disconnected: { bars: 0, color: "bg-muted", label: "Connecting..." },
};

const ConnectionQualityIndicator = memo(({
  quality,
  showLabel = true,
}: ConnectionQualityIndicatorProps) => {
  const config = qualityConfig[quality];

  if (quality === "disconnected") {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm">
        <WifiOff className="w-4 h-4 text-muted-foreground animate-pulse" />
        {showLabel && (
          <span className="text-xs text-muted-foreground">{config.label}</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm">
      <Wifi className={`w-4 h-4 ${quality === "poor" ? "text-red-500" : quality === "fair" ? "text-yellow-500" : "text-green-500"}`} />
      
      {/* Signal bars */}
      <div className="flex items-end gap-0.5 h-4">
        {[1, 2, 3, 4].map((bar) => (
          <div
            key={bar}
            className={`w-1 rounded-sm transition-all ${
              bar <= config.bars ? config.color : "bg-white/20"
            }`}
            style={{ height: `${bar * 25}%` }}
          />
        ))}
      </div>

      {showLabel && (
        <span className={`text-xs ${
          quality === "poor" ? "text-red-400" : 
          quality === "fair" ? "text-yellow-400" : 
          "text-green-400"
        }`}>
          {config.label}
        </span>
      )}
    </div>
  );
});

ConnectionQualityIndicator.displayName = "ConnectionQualityIndicator";

export default ConnectionQualityIndicator;
